import { Component, OnInit, PLATFORM_ID, OnDestroy, inject } from '@angular/core';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { CartItem, CartItemOptionItems, SharedOption } from '@pedix-workspace/utils';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { ToastrService } from 'ngx-toastr';
import { cloneDeep } from 'lodash-es';

import { filter, map } from 'rxjs/operators';
import { isPlatformServer, NgClass, AsyncPipe, isPlatformBrowser } from '@angular/common';
import { TranslocoService, TranslocoDirective } from '@ngneat/transloco';
import { nanoid } from 'nanoid';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { SafeUrl } from '@angular/platform-browser';
import {
  EstablishmentUrlPipe,
  IsCategoryAvailablePipe,
} from '@pedix-workspace/pedixapp-shared-pipes';
import { AnalyticsService } from '@pedix-workspace/pedixapp-core-services';
import { StockService } from '@pedix-workspace/pedixapp-stock';
import { StockInfo, StockMap, StockProduct } from '@pedix-workspace/shared-stock';
import { SeoService } from '../../main/seo.service';
import { EstablishmentWorkingHoursService } from '../../main/establishment-working-hours.service';
import { WhatsappLinkService } from '@pedix-workspace/angular-utils';
import { ApiV1_Category, ApiV1_Establishment, ApiV1_Product } from '@pedix-workspace/api-v1';
import { isProductWithoutStock } from '@pedix-workspace/shared-models';
import { PropInSelectedLanguagePipe } from '@pedix-workspace/pedixapp-shared-pipes';
import {
  ProductTagComponent,
  ProductDetailsComponent,
  ProductQuantitySelectorComponent,
  CategoryAvailabilityDialogComponent,
} from '@pedix-workspace/pedixapp-shared-catalog';
import { ButtonComponent } from '@pedix-workspace/angular-ui-button';
import {
  IconBackComponent,
  IconClockComponent,
  IconLoadingComponent,
  IconShareComponent,
} from '@pedix-workspace/angular-ui-icons';
import { AppRequestContextService } from '../../../app-request-context.service';
import { AppCartItemsService } from '../../../app-cart-items.service';
import { FormsModule, NgForm } from '@angular/forms';
import { ProductTagsWithQuantitiesPipe } from '@pedix-workspace/pedixapp-shared-pipes';
import { AlertComponent } from '@pedix-workspace/angular-ui-alert';
import { createOrUpdateCartItem } from '@pedix-workspace/orders';
import { toObservable } from '@angular/core/rxjs-interop';

@UntilDestroy()
@Component({
    selector: 'app-product',
    templateUrl: './product.component.html',
    styleUrls: ['./product.component.scss'],
    imports: [
        TranslocoDirective,
        NgClass,
        IconBackComponent,
        IconShareComponent,
        IconLoadingComponent,
        ButtonComponent,
        ProductQuantitySelectorComponent,
        AsyncPipe,
        PropInSelectedLanguagePipe,
        IsCategoryAvailablePipe,
        FormsModule,
        RouterLink,
        ProductTagsWithQuantitiesPipe,
        ProductTagComponent,
        ProductDetailsComponent,
        AlertComponent,
        IconClockComponent,
        CategoryAvailabilityDialogComponent,
    ],
    // ProductComponent uses its own version of StockService (check providers[]); this is on purpose, leave it this way
    providers: [StockService]
})
export class ProductComponent implements OnInit, OnDestroy {
  paramsInput$ = new BehaviorSubject<{ categoryId: string; productId: string }>(undefined);
  product: ApiV1_Product;
  category: ApiV1_Category;
  sharedOptions: SharedOption[];
  cartItem: CartItem;
  cartItemId: string;
  productQuantity = 1;
  productObservations = '';
  editMode = false;
  selectedOptions: CartItemOptionItems[] = [];
  selectedPresentationOption: CartItemOptionItems;
  isCartDisabled = false;
  productShareUrl: SafeUrl;
  stockInfo: StockInfo;
  hasStockIssues: boolean;
  isWithoutStock = false;
  cartItemOverride$ = new Subject<CartItem>();
  stock$: Observable<StockMap>;
  isFormSubmitted = false;

  get whatsappLinkHttpTarget() {
    return this.whatsappLinkService.getWhatsappLinkHttpTarget();
  }

  get establishment(): ApiV1_Establishment {
    return this.appRequestContext.establishment;
  }

  get displayShareButton(): boolean {
    return this.establishment.seoSocialMedia
      ? this.establishment.seoSocialMedia.displayShareButton
      : true;
  }

  get selectedLanguage() {
    return this.appRequestContext.selectedLanguage;
  }

  get activeProducts(): ApiV1_Product[] {
    return this.appRequestContext.activeProducts;
  }

  get maxQuantity(): number | undefined {
    if (this.product.presentations) {
      if (!this.product.presentations.displayItemQuantities && this.selectedPresentationOption) {
        return this.selectedPresentationOption.items[0].stockQty ?? undefined;
      }
    } else {
      return this.product.stockQty ?? undefined;
    }
    return undefined;
  }

  get displaySku(): boolean {
    return this.appRequestContext.shouldDisplaySku;
  }

  get displayProductPriceWithoutTaxes(): boolean {
    return this.establishment.catalogConfiguration.displayProductPriceWithoutTaxes;
  }

  private platformId = inject(PLATFORM_ID);

  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private toastrService = inject(ToastrService);
  private analytics = inject(AnalyticsService);
  private seoService = inject(SeoService);
  private t = inject(TranslocoService);
  private whatsappLinkService = inject(WhatsappLinkService);
  private establishmentUrlPipe = inject(EstablishmentUrlPipe);
  private appRequestContext = inject(AppRequestContextService);
  private stockService = inject(StockService);
  private establishmentWorkingHours = inject(EstablishmentWorkingHoursService);
  private appCartItemsService = inject(AppCartItemsService);

  constructor() {
    this.stock$ = toObservable(this.stockService.stock);
  }

  ngOnInit() {
    this.stockService.products.set(this.appRequestContext.products as StockProduct[]);

    this.cartItemOverride$.subscribe(cartItemOverride => {
      const cartItemsCopy = [...this.appCartItemsService.cartItems];

      const index = cartItemsCopy.findIndex(cartItem => cartItem.id === cartItemOverride.id);

      if (index !== -1) {
        cartItemsCopy.splice(index, 1, cartItemOverride);
      } else {
        cartItemsCopy.push(cartItemOverride);
      }

      this.stockService.cartItems.set(cloneDeep(cartItemsCopy));
    });

    // Produces productId$ next value on route change
    this.activatedRoute.params.pipe(untilDestroyed(this)).subscribe(params => {
      if (params.categoryId && params.productId) {
        this.cartItemId = nanoid();
        this.paramsInput$.next({
          categoryId: params.categoryId,
          productId: params.productId,
        });
      } else if (params.cartItemId && !isPlatformServer(this.platformId)) {
        const cartItem = this.appCartItemsService.findCartItem(params.cartItemId);

        this.editMode = true;
        this.cartItem = cloneDeep(cartItem);
        this.cartItemId = cartItem.id;
        this.productQuantity = this.cartItem.qty;
        this.productObservations = this.cartItem.observations;
        this.selectedPresentationOption = this.cartItem.selectedPresentation;

        if (!this.cartItem) {
          this.router.navigate(['../../'], { relativeTo: this.activatedRoute });
          return;
        }
        this.cartItemOverride$.next(this.cartItem);

        this.paramsInput$.next({
          categoryId: this.cartItem.category.id,
          productId: this.cartItem.product.id,
        });
      }
    });

    this.paramsInput$
      .pipe(
        filter(params => {
          return !!params?.productId;
        }),
        map(({ categoryId, productId }) => {
          const category = this.appRequestContext.categories.find(c => c.id === categoryId);
          const product = category.products.find(
            categoryProduct => categoryProduct.id === productId,
          );

          return [category, product] as const;
        }),
        untilDestroyed(this),
      )
      .subscribe(([category, product]) => {
        this.category = category;
        this.product = cloneDeep(product);
        this.isWithoutStock = isProductWithoutStock({
          stockQty: this.product.stockQty, // TODO: this should be using stockInfo.availableStock
          presentationItems: this.product?.presentations?.items,
        });

        this.productShareUrl = this.getProductShareUrl();

        this.seoService.setProductMetadata(product, this.establishment);
        this.analytics.viewProduct(
          product,
          category,
          this.establishment.categories ? this.establishment.categories[0] : null,
        );

        if (this.cartItem?.selectedOptions) {
          this.selectedOptions = this.product.options.map(embeededOption =>
            this.cartItem.selectedOptions.find(
              cartItemOption => cartItemOption.option.id === embeededOption.id,
            ),
          );
        }
      });

    if (isPlatformBrowser(this.platformId)) {
      combineLatest([this.paramsInput$, this.stock$])
        .pipe(untilDestroyed(this))
        .subscribe(([{ productId }, stock]) => {
          this.stockInfo = stock.getStockInfoByItem(productId);

          const cartItem = this.createOrUpdateCartItem();

          this.hasStockIssues = stock
            .getStockInfoArrayByCartItem(cartItem.id)
            .some(stockInfo => stockInfo.getMissingStockByCartItem(this.cartItemId) > 0);
        });
    }

    this.establishmentWorkingHours.openedStatus$
      .pipe(untilDestroyed(this))
      .subscribe(({ isCartDisabled }) => {
        this.isCartDisabled = isCartDisabled;
      });
  }

  onStockSelectionChanged() {
    if (isPlatformServer(this.platformId)) {
      return;
    }
    const cartItem = this.createOrUpdateCartItem();

    this.cartItemOverride$.next(cartItem);
  }

  ngOnDestroy() {
    this.seoService.setDefaultMetadata();
  }

  back() {
    if (this.editMode) {
      this.router.navigate([`../../`], { relativeTo: this.activatedRoute });
    } else {
      if (
        this.activatedRoute.snapshot.queryParams['q'] ||
        this.activatedRoute.snapshot.queryParams['qt']
      ) {
        this.router.navigate([`/busqueda`], {
          queryParams: {
            q: this.activatedRoute.snapshot.queryParams['q'],
            qt: this.activatedRoute.snapshot.queryParams['qt'],
          },
        });
      } else if (this.activatedRoute.snapshot.queryParams['t']) {
        this.router.navigate([`/productos-relacionados`], {
          queryParams: { t: this.activatedRoute.snapshot.queryParams['t'] },
        });
      } else {
        this.router.navigate(['../..'], {
          relativeTo: this.activatedRoute,
        });
      }
    }
  }

  addToCart($event: FormDataEvent, productForm: NgForm, product: ApiV1_Product) {
    this.isFormSubmitted = true;

    if (!productForm.valid) {
      this.toastrService.error(this.t.translate('validation.fixErrors'));

      return;
    }
    const cartItem = this.createOrUpdateCartItem();

    if (this.editMode) {
      this.appCartItemsService.updateCartItem(cartItem);
    } else {
      this.appCartItemsService.addCartItem(cartItem);
    }
    this.back();
  }

  private createOrUpdateCartItem(): CartItem {
    return createOrUpdateCartItem({
      category: this.category,
      product: this.product,
      establishmentId: this.establishment.id,
      cartItemId: this.cartItemId,
      selectedPresentationOption: this.selectedPresentationOption,
      productQuantity: this.productQuantity,
      productObservations: this.productObservations,
      selectedOptions: this.selectedOptions,
    });
  }

  private getProductShareUrl() {
    const productUrl = this.establishmentUrlPipe.transform(this.establishment, [
      'categoria',
      this.category.id,
      'producto',
      this.product.id,
    ]);

    const text = this.t.translate('product.shareText', { productUrl });

    return this.whatsappLinkService.getTrustedLink({ text });
  }
}
